import { Button } from '../../../../../components/ui/button';
import { trackStorageEvent } from '../../services/storageAnalytics';
import { useStorageDialogStore, type StorageDialogRequest } from './storageDialogStore';

const LABELS: Record<StorageDialogRequest['kind'], string> = {
  properties: 'Properties', tier: 'Change tier', 'public-access': 'Set public access level', 'access-policies': 'Manage access policies', sas: 'Generate SAS',
  'hns-new-directory': 'New folder', 'hns-move': 'Rename or move', 'hns-acl': 'Manage access', 'hns-acl-propagate': 'Propagate access', // S8
};
const describe = (request: StorageDialogRequest) => (request.kind === 'sas' && request.source === 'mcp' ? 'the SAS an agent generated' : LABELS[request.kind]);

/**
 * Shown INSIDE the busy dialog (Fable F8), so using it never counts as a click outside a modal: the openings waiting
 * behind this dialog's draft or save, and the "discard?" question for one that would replace it.
 */
export function QueuedDialogNotice() {
  const { queued, confirming, guard, viewQueued, confirmReplacement, keepEditing } = useStorageDialogStore();
  const next = queued[0];
  if (confirming) {
    const act = (action: 'discard_and_open' | 'keep_editing', run: () => void) => () => { trackStorageEvent('feature:storage_dialog_queue', { action, kind: confirming.kind }); run(); };
    return <div role="alert" data-testid="storage-dialog-discard-confirm" className="rounded border border-warning/40 bg-warning/10 p-2 text-xs">
      Opening {describe(confirming)} discards your unsaved changes here.
      <div className="mt-1 flex gap-2">
        <Button type="button" size="xs" variant="destructive" disabled={guard.saving} title={guard.saving ? 'Wait for the save to finish' : undefined} onClick={act('discard_and_open', confirmReplacement)}>Discard and open</Button>
        <Button type="button" size="xs" variant="outline" onClick={act('keep_editing', keepEditing)}>Keep editing</Button>
      </div>
    </div>;
  }
  if (!next) return null;
  const text = next.kind === 'sas' && next.source === 'mcp' ? 'A SAS was generated by an agent' : `${LABELS[next.kind]} is waiting`;
  const view = () => { trackStorageEvent('feature:storage_dialog_queue', { action: 'view', kind: next.kind }); viewQueued(); };
  return <div role="status" data-testid="storage-dialog-queued-notice" className="flex items-center justify-between gap-2 rounded border border-border bg-muted/50 p-2 text-xs">
    <span>{text}{queued.length > 1 ? ` (${queued.length})` : ''} — it opens when you close this dialog.</span>
    <Button type="button" size="xs" variant="outline" disabled={guard.saving} title={guard.saving ? 'Wait for the save to finish' : undefined} onClick={view}>View</Button>
  </div>;
}
